// English is the canonical URL shape (`/about`), while translated pages are prefixed (`/es/about`).
const DEFAULT_LANGUAGE = 'en';

// Builds a localized URL path from an existing path.
// Why: keeps every internal link and history update in a consistent format that Weglot can follow.
const languageUri = (currentUri, language) => {
  // Strip known language prefixes so we can rebuild the path from a clean base.
  const removeUriLanguage = uri => uri.replace(/\/(es)(\/|$)/g, '');

  // Prevent double trailing slashes after path transformations.
  const removeTrailingSlash = uri => uri.replace(/\/$/g, '');

  // Normalize to absolute paths before concatenating prefixes.
  const addLeadingSlash = uri => {
    if (uri.match(/^\//)) {
      return uri;
    }

    return `/${uri}`;
  };

  // Keep default-language URLs short and stable; add prefixes only for non-default languages.
  const languageUriPrefix = language => {
    if (language === DEFAULT_LANGUAGE) {
      return '';
    }

    return addLeadingSlash(language);
  };

  return (
    `${languageUriPrefix(language)}${removeTrailingSlash(
      addLeadingSlash(removeUriLanguage(currentUri)),
    )}` || '/'
  );
};

// Rewrites all internal `<a>` links to point at the active language.
// Why: users should stay in the same language when navigating within the site.
export const updateAnchorLinks = language => {
  if (!language) {
    // Fallback to Weglot state when the caller does not provide a language.
    language = window.Weglot.getCurrentLang();
  }

  const anchors = () => document.querySelectorAll('a');

  const internalLink = anchor => anchor.hostname === window.location.hostname;

  const relativeLocation = location => location.href.replace(location.origin, '');

  for (const anchor of anchors()) {
    if (internalLink(anchor)) {
      anchor.href = languageUri(relativeLocation(anchor), language);
    }
  }
};

// Mirrors the active language in the browser URL without a full reload.
// Why: back/forward navigation and shared links should reflect the current language context.
const updateAddressBar = language => {
  history.pushState({}, document.title, languageUri(window.location.pathname, language));
};

// Reads language intent directly from the current URL path.
// Why: URL is the source of truth for deep links and navigation state.
const detectLanguageFromUrl = () => {
  const path = window.location.pathname;
  if (path.match(/^\/es(\/|$)/)) {
    return 'es';
  }
  return DEFAULT_LANGUAGE;
};

// Keeps Weglot language in sync with language encoded in the URL.
// Why: prevents mismatch between visible content language and path prefix.
const handleUrlLanguageSync = () => {
  if (!window.Weglot || !window.Weglot.getCurrentLang) return;
  
  const urlLanguage = detectLanguageFromUrl();
  const currentWeglotLang = window.Weglot.getCurrentLang();
  
  // Only switch if there's a mismatch and avoid infinite loops
  if (urlLanguage !== currentWeglotLang) {
    window.Weglot.switchTo(urlLanguage);
  } else {
    // Intentional no-op when URL and Weglot are already aligned.
  }
};

// Responds to Weglot language switches by updating URL and internal links.
// Why: Weglot can change content language without changing navigation URLs on its own.
const languageChanged = (newLang, prevLang) => {
  // Weglot fires `source -> en` during startup; treat it as initialization, not a user change.
  const noLanguageChange = newLang === DEFAULT_LANGUAGE && prevLang === 'source';
  if (noLanguageChange) {
    return;
  }

  updateAddressBar(newLang);
  updateAnchorLinks(newLang);
};

// Guards against duplicate event listener registration.
let isInitialized = false;
// Tracks the last path the user intended to visit (useful during async translation switches).
let originalPath = window.location.pathname;

// Infers desired language from a path that may come from click/popstate events.
const detectIntendedLanguage = (path) => {
  if (path.match(/^\/es(\/|$)/)) {
    return 'es';
  }
  return DEFAULT_LANGUAGE;
};

// Forces Weglot to match the language implied by the intended path.
// Why: navigation can happen faster than Weglot's internal state propagation.
const forceLanguageBasedOnIntent = () => {
  const intendedLang = detectIntendedLanguage(originalPath);
  const currentWeglotLang = window.Weglot ? window.Weglot.getCurrentLang() : DEFAULT_LANGUAGE;
  
  if (intendedLang !== currentWeglotLang) {
    window.Weglot.switchTo(intendedLang);
  }
};

// One-time i18n bootstrap.
// Why: wires Weglot + browser events so content language, URLs, and links stay synchronized.
const initI18n = () => {
  if (isInitialized) return;
  isInitialized = true;
  
  // React to language changes initiated by the switcher or programmatic `switchTo`.
  Weglot.on('languageChanged', (newLang, prevLang) => {
    languageChanged(newLang, prevLang);
  });
  
  // On back/forward, derive target language from new path and enforce it after route settles.
  window.addEventListener('popstate', () => {
    originalPath = window.location.pathname;
    setTimeout(forceLanguageBasedOnIntent, 50);
  });
  
  // Capture internal click intent early so Weglot can switch language during navigation.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (link && link.hostname === window.location.hostname) {
      const url = new URL(link.href);
      originalPath = url.pathname;
      setTimeout(forceLanguageBasedOnIntent, 50);
    }
  });
  
  // Final startup alignment once Weglot internals and switchers are fully ready.
  setTimeout(() => {
    forceLanguageBasedOnIntent();
    updateAnchorLinks(detectIntendedLanguage(originalPath));
  }, 100);
};

if (window.Weglot) {
  // Primary initialization point when Weglot core finishes loading.
  Weglot.on('initialized', () => {
    initI18n();
  });

  // Secondary initialization point for themes that load switchers later.
  Weglot.on('switchersReady', () => {
    initI18n();
  });

  // Fallback for cases where this script runs after Weglot already initialized.
  if (window.Weglot && window.Weglot.initialized) {
    initI18n();
  }
}
